import { AppAction, Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { AppLinkOutConfig } from '../../apis/eols-app-link/eols-app-link.models';
import { NavigateToApp } from '../../redux/courseware/courseware.models';
import { isInstructor, stripNilProps } from '../../utilities/common.utilities';
import { ApplicationNavigateHandlerMapProps,
  CoursePlanClickSettings,
  CoursePlanPerformanceReportClickProps,
  CoursePlanTitleClickProps,
  EntityProps,
  SharedAppLinkParams,
  SherEvolveAssignmentPerformanceView,
  SortCatalogItems } from '../model';
import { AssessmentDto, AssessmentStatusDto, AssignmentDto, AssignmentType } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { RoutePath } from '../../components/app/app.constants';
import { RecContentItemDto } from '../../apis/rec-gateway/rec-gateway.dtos';
import { CatalogWithExternalEntitiesDto,
  CommonSLSExternalEntityDtoParsed } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import { CatalogPageTitleClickProps,
  GetAssignmentInfoProps,
  RecContentItemTitleHandlerProps,
  RecContentItemTitleHandlerPropsClone } from '../../models/assignment-types-shared.models';
import { getCatalogItemFromSyllabusItem,
  getShadowHealth,
  getShadowHealthClone,
  getSimulationLearningSystemResource,
  getSimulationLearningSystemResourceClone,
  isOpenAuthessInNewTab } from '../../utilities/assignment-types-shared.utilities';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { initiateAssessmentPayloadForEAQ, integrationMetadataForEAQ, nextRedirectPayloadForEAQ } from '../../apis/eaq-app-facade-service/eaq-app-facade-service.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { getOsmosisTokenAndConfigPromiseForSyllabus } from '../../pages/course-plan/assignment-type-syllabus-shared.utils';
import { AssignmentEditorSearchParam } from '../../hocs/with-base-assignment-editor/with-base-assignment-editor.constants';
import { addSearchParams } from '../../utilities/app.utilities';
import { PrimaryTaxonomy } from '../../apis/rec-gateway/rec-gateway.models';
import { EvolveProductDto } from '../../apis/sherpath-app-facade-service/sherpath-app-facade-service.dtos';
import { ActiveSyllabusItemTypeDto } from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { assignableSimulationSubTypes } from '../../constants/assignment-types.constants';
import { getCommonExternalEntity, getSimchartExternalEntity } from './external-entity.handlers';

const isAuthessAssignment = (assignment?: AssignmentDto, isAuthessEnabled?: boolean) =>
  Boolean(isAuthessEnabled && assignment && assignment.assignmentType === AssignmentType.AUTHESS);

const isAssessmentCompleted = (assessment?: AssessmentDto) =>
  Boolean(assessment && assessment.status === AssessmentStatusDto.COMPLETED);

const selectTitleClickDestination = (userRole: string, opts: {
  isAuthess: boolean;
  isEnableEAQAssignmentManagementApp: boolean;
  isEaqStudentPerformanceEnabled: boolean;
  isEaqAssessmentPlayerEnabled: boolean;
  isCompleted: boolean;
  assessment: AssessmentDto;
}): { app: Application; action: AppAction } => {
  const isUserInstructor = isInstructor(userRole);
  if (isUserInstructor && opts.isAuthess) return { app: Application.AUTHESS, action: AppAction.ASSIGNMENT_PREVIEW };
  if (!isUserInstructor && opts.isAuthess) return { app: Application.AUTHESS, action: AppAction.ASSESSMENT_START };
  if (isUserInstructor && opts.isEnableEAQAssignmentManagementApp) return { app: Application.ASSIGNMENT_MANAGEMENT, action: AppAction.ASSIGNMENT_PREVIEW };
  if (isUserInstructor && !opts.isEnableEAQAssignmentManagementApp) return { app: Application.EAQ, action: AppAction.ASSIGNMENT_PREVIEW };
  if (!isUserInstructor && opts.isCompleted && opts.isEaqStudentPerformanceEnabled) return { app: Application.STUDENT_STUDY, action: AppAction.ASSESSMENT_PERFORMANCE_VIEW };
  if (!isUserInstructor && (!opts.isCompleted || !opts.assessment) && opts.isEaqAssessmentPlayerEnabled) {
    return { app: Application.NHE_ASSESSMENT_PLAYER, action: AppAction.ASSESSMENT_START };
  }
  return { app: Application.EAQ, action: AppAction.ASSESSMENT_START };
};

const selectPerformanceDestination = (opts: {
  isUserRoleInstructor: boolean;
  isAuthess: boolean;
  isAmaAssignmentDetailEnabled: boolean;
  isEaqAssessmentPlayerEnabled: boolean;
  isCompleted: boolean;
  isEaqStudentPerformanceEnabled: boolean;
  assessment: AssessmentDto;
}): { app: Application; action: AppAction } => {
  if (opts.isAuthess) return { app: Application.AUTHESS, action: AppAction.ASSIGNMENT_PERFORMANCE_VIEW };
  if (opts.isUserRoleInstructor && opts.isAmaAssignmentDetailEnabled) return { app: Application.ASSIGNMENT_MANAGEMENT, action: AppAction.ASSIGNMENT_PERFORMANCE_VIEW };
  if (!opts.isUserRoleInstructor && (!opts.isCompleted || !opts.assessment) && opts.isEaqAssessmentPlayerEnabled) {
    return { app: Application.NHE_ASSESSMENT_PLAYER, action: AppAction.ASSESSMENT_START };
  }
  if (!opts.isUserRoleInstructor && opts.isCompleted && opts.isEaqStudentPerformanceEnabled) {
    return { app: Application.STUDENT_STUDY, action: AppAction.ASSESSMENT_PERFORMANCE_VIEW };
  }
  return { app: Application.EAQ, action: AppAction.ASSIGNMENT_PERFORMANCE_VIEW };
};

export const navigateToSimchart = (userRole: string, navigateToApp: NavigateToApp, sharedAppLinkParams: SharedAppLinkParams) => {
  const action = isInstructor(userRole) ? AppAction.ASSIGNMENT_PREVIEW : AppAction.ASSESSMENT_START;
  return Promise.resolve(
    navigateToApp({
      app: Application.SIMSNG,
      action,
      body: sharedAppLinkParams
    })
  );
};

export const simchartPerformanceReportClick = (navigateToApp: NavigateToApp, sharedAppLinkParams: SharedAppLinkParams) => {
  const action = AppAction.ASSIGNMENT_PERFORMANCE_VIEW;
  navigateToApp({
    app: Application.SIMSNG,
    action,
    body: sharedAppLinkParams
  });
};

export const simchartEditClick = (assignment: AssignmentDto, navigateToApp: NavigateToApp, sharedAppLinkParams: SharedAppLinkParams) => {
  const action = assignment ? AppAction.ASSIGNMENT_EDIT : AppAction.ASSIGNMENT_CREATE;
  navigateToApp({
    app: Application.SIMSNG,
    action,
    body: sharedAppLinkParams
  });
};

export const simchartCatalogTitleClick = (navigateToApp: NavigateToApp, contentItem: RecContentItemDto) => {
  navigateToApp({
    app: Application.SIMSNG,
    action: AppAction.ASSIGNMENT_PREVIEW,
    body: stripNilProps({
      catalogItem: contentItem,
      ref: RoutePath.CATALOG
    })
  });
};

export const simchartExternalEntity = (props: EntityProps) => {
  return getSimchartExternalEntity(props);
};

export const navigateExternal = (props: ApplicationNavigateHandlerMapProps, extraAppLinkData?: Partial<AppLinkOutConfig>) => {
  const { defArgs, isDifferentCourse, appLinkConfig, assignment, newToken } = props;
  let modifiedAppLinkConfig = appLinkConfig;

  if (extraAppLinkData) {
    modifiedAppLinkConfig = {
      ...appLinkConfig,
      ...extraAppLinkData,
      body: {
        ...appLinkConfig.body,
        ...extraAppLinkData.body
      }
    };
  }

  if (isDifferentCourse) {
    defArgs.props.navigateToApp(
      modifiedAppLinkConfig,
      isDifferentCourse, // isNewTab
      newToken
    );
  } else {
    defArgs.props.updateAssignmentIdRedirected(assignment?.id?.toString() ?? '');
    defArgs.props.navigateToApp(modifiedAppLinkConfig);
  }
};

export const redirectInternal = (props: ApplicationNavigateHandlerMapProps, customPath?: RoutePath, extraParams?: { [x: string]: unknown }) => {
  const { syllabusItem, assignment, defArgs } = props;
  const searchParams = {
    [AssignmentEditorSearchParam.SYLLABUS_ITEM_ID]: syllabusItem.id,
    [AssignmentEditorSearchParam.ASSIGNMENT_ID]: assignment ? assignment.id : null,
    [AssignmentEditorSearchParam.REF]: RoutePath.ASSIGNMENT_OVERVIEW,
    ...extraParams
  };
  defArgs.props.updateAssignmentIdRedirected(assignment?.id?.toString() ?? '');
  defArgs.props.redirect(addSearchParams(customPath ?? RoutePath.ASSIGNMENT_PERFORMANCE_REPORT, searchParams));
};

export const getCommonSimulationLearningSystemTitleHandler = (props: RecContentItemTitleHandlerProps): string | null => {
  const simulationLearningSystemResource = getSimulationLearningSystemResource(props.contentItem, props.catalog) as CommonSLSExternalEntityDtoParsed;
  if (simulationLearningSystemResource) {
    return simulationLearningSystemResource._parsedData.title;
  }
  return null;
};

export const getCommonSimulationLearningSystemTitleHandlerClone = (props: RecContentItemTitleHandlerPropsClone): string | null => {
  const simulationLearningSystemResource = getSimulationLearningSystemResourceClone(props.contentItem, props.externalEntitiesMap) as CommonSLSExternalEntityDtoParsed;
  if (simulationLearningSystemResource) {
    return simulationLearningSystemResource._parsedData.title;
  }
  return null;
};

export const adaptiveQuizTitleClick = async (props: CoursePlanTitleClickProps) => {
  const { assignment, defArgs, sharedAppLinkParams } = props;
  const { userRole, featureFlagsGrouped, courseSectionId, userId, fetchRecentAssessmentOfAssignment, navigateToApp } = defArgs.props;
  const flag = isInstructor(userRole)
    ? FEATURE_FLAG.IS_EAQ_AUTHESS_INS_APP_LINK_ENABLED
    : FEATURE_FLAG.IS_EAQ_AUTHESS_APP_LINK_ENABLED;
  const isAuthessEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    flag,
    courseSectionId
  );
  const isNewTabEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.IS_EAQ_AUTHESS_APP_LINK_STUDENT_OPEN_NEW_TAB_ENABLED,
    courseSectionId
  );
  const isAddAuthessFlagToOutpostBody = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.IS_ADD_AUTHESS_FLAG_TO_OUTPOST_BODY,
    courseSectionId
  );
  const isAuthess = isAuthessAssignment(assignment, isAuthessEnabled);
  const isNewTab = isOpenAuthessInNewTab({
    isNewTabEnabled,
    isAuthessEnabled,
    userRole,
    assignment
  });
  const isEnableEAQAssignmentManagementApp = featureFlagsGrouped && courseSectionId ? getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_EAQ_ASSIGNMENT_MANAGEMENT_APP_CREATE_EDIT,
    courseSectionId
  ) : false;
  const isEaqAssessmentPlayerEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.EAQ_ASSESSMENT_PLAYER_REDIRECT,
    courseSectionId
  );
  const isEaqStudentPerformanceEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_STUDENT_PERFORMANCE_DETAILS,
    courseSectionId
  );

  const assessment: AssessmentDto = await fetchRecentAssessmentOfAssignment(userId, assignment.id);
  const isCompleted = isAssessmentCompleted(assessment);

  const { app, action } = selectTitleClickDestination(userRole, {
    isAuthess,
    isEnableEAQAssignmentManagementApp,
    isEaqStudentPerformanceEnabled,
    isEaqAssessmentPlayerEnabled,
    isCompleted,
    assessment
  });

  return Promise.resolve(
    navigateToApp({
      app,
      includeLinkHash: true,
      action,
      body: stripNilProps({
        ...sharedAppLinkParams,
        isAuthess: isAddAuthessFlagToOutpostBody
          ? Boolean(assignment && assignment.assignmentType === AssignmentType.AUTHESS)
          : null,
        nextRedirectPayload: app === Application.NHE_ASSESSMENT_PLAYER ? nextRedirectPayloadForEAQ : null,
        initiateAssessment:
          app === Application.NHE_ASSESSMENT_PLAYER ? initiateAssessmentPayloadForEAQ(sharedAppLinkParams.assignmentId, assessment ? assessment.id : null) : null,
        integrationMetadata: app === Application.NHE_ASSESSMENT_PLAYER ? integrationMetadataForEAQ : null
      })
    }, isNewTab)
  );
};

export const adaptiveQuizPerformanceReportClick = async (props: CoursePlanPerformanceReportClickProps) => {
  const { defArgs, sharedAppLinkParams, assignment } = props;
  const { userRole, featureFlagsGrouped, courseSectionId, userId, fetchRecentAssessmentOfAssignment, navigateToApp } = defArgs.props;
  const isUserRoleInstructor = isInstructor(userRole);
  const flag = isUserRoleInstructor
    ? FEATURE_FLAG.IS_EAQ_AUTHESS_INS_APP_LINK_ENABLED
    : FEATURE_FLAG.IS_EAQ_AUTHESS_APP_LINK_ENABLED;
  const isAuthessEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    flag,
    courseSectionId
  );
  const isNewTabEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.IS_EAQ_AUTHESS_APP_LINK_STUDENT_OPEN_NEW_TAB_ENABLED,
    courseSectionId
  );
  const isAddAuthessFlagToOutpostBody = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.IS_ADD_AUTHESS_FLAG_TO_OUTPOST_BODY,
    courseSectionId
  );
  const isAuthess = isAuthessAssignment(assignment, isAuthessEnabled);
  const isNewTab = isOpenAuthessInNewTab({
    isNewTabEnabled,
    isAuthessEnabled,
    userRole,
    assignment
  });
  const isAmaAssignmentDetailEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_AMA_ASSIGNMENT_DETAIL,
    courseSectionId
  );
  const isEaqAssessmentPlayerEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.EAQ_ASSESSMENT_PLAYER_REDIRECT,
    courseSectionId
  );
  const isEaqStudentPerformanceEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_STUDENT_PERFORMANCE_DETAILS,
    courseSectionId
  );

  const assessment: AssessmentDto = await fetchRecentAssessmentOfAssignment(userId, assignment.id);
  const isCompleted = isAssessmentCompleted(assessment);

  const { app, action } = selectPerformanceDestination({
    isUserRoleInstructor,
    isAuthess,
    isAmaAssignmentDetailEnabled,
    isEaqAssessmentPlayerEnabled,
    isCompleted,
    isEaqStudentPerformanceEnabled,
    assessment
  });

  navigateToApp({
    app,
    includeLinkHash: true,
    action,
    body: stripNilProps({
      ...sharedAppLinkParams,
      isAuthess: isAddAuthessFlagToOutpostBody
        ? Boolean(assignment && assignment.assignmentType === AssignmentType.AUTHESS)
        : null,
      nextRedirectPayload: app === Application.NHE_ASSESSMENT_PLAYER ? nextRedirectPayloadForEAQ : null,
      initiateAssessment:
            app === Application.NHE_ASSESSMENT_PLAYER ? initiateAssessmentPayloadForEAQ(sharedAppLinkParams.assignmentId, assessment ? assessment.id : null) : null,
      integrationMetadata: app === Application.NHE_ASSESSMENT_PLAYER ? integrationMetadataForEAQ : null
    })
  }, isNewTab);
};

export const sherpathLessonTitleClick = (props: CoursePlanTitleClickProps) => {
  const { defArgs, sharedAppLinkParams, isOsmosisVideosEnabled, syllabusItem } = props;
  const { fetchOsmosisTokenAction, catalog, assignments, primaryTaxonomies, evolveProducts, userRole, navigateToApp } = defArgs.props;
  const promise = getOsmosisTokenAndConfigPromiseForSyllabus({
    fetchOsmosisTokenAction,
    isOsmosisVideosEnabled,
    syllabusItem,
    catalog,
    assignments,
    primaryTaxonomies,
    evolveProducts
  });
  return promise.then((response) => {
    const osmosisToken = response ? response.osmosisToken.authToken : null;
    const osmosisIncludesConfigs = response ? response.osmosisIncludesConfigs : null;
    const sherpathOutProps = stripNilProps({
      ...sharedAppLinkParams,
      osmosisToken,
      osmosisIncludesConfigs
    });
    const action = isInstructor(userRole) ? AppAction.ASSIGNMENT_PREVIEW : AppAction.ASSESSMENT_START;
    navigateToApp({
      app: Application.SHERPATH,
      action,
      body: sherpathOutProps
    });
  });
};

export const navigateToPerformanceReport = (props: CoursePlanPerformanceReportClickProps) => {
  const { defArgs, syllabusItem, assignment } = props;
  const { redirect } = defArgs.props;
  const searchParams = {
    [AssignmentEditorSearchParam.SYLLABUS_ITEM_ID]: syllabusItem.id,
    [AssignmentEditorSearchParam.ASSIGNMENT_ID]: assignment ? assignment.id : null
  };
  redirect(addSearchParams(RoutePath.ASSIGNMENT_PERFORMANCE_REPORT, searchParams));
};

export const sherEvolPerformanceViewRedirect = (props: SherEvolveAssignmentPerformanceView) => {
  const { _syllabusItemId, assignmentId, ref, redirect } = props;
  redirect(addSearchParams(RoutePath.ASSIGNMENT_PERFORMANCE_REPORT, {
    [AssignmentEditorSearchParam.SYLLABUS_ITEM_ID]: _syllabusItemId,
    [AssignmentEditorSearchParam.ASSIGNMENT_ID]: assignmentId,
    [AssignmentEditorSearchParam.REF]: ref,
  }));
};

export const sortCatalogForSequenceTypes = (props: SortCatalogItems) => {
  const { contentItem, itemSequenceMap } = props;
  if (!itemSequenceMap || !contentItem.attributes?.contentId) {
    return null;
  }
  const sequenceItem = itemSequenceMap[contentItem.attributes.contentId];
  if (!sequenceItem) {
    return null;
  }
  return sequenceItem.sequenceIndex;
};

export const shadowHealthTitleClick = (props: CoursePlanTitleClickProps) => {
  const { defArgs, sharedAppLinkParams } = props;
  const { userRole, navigateToApp, course } = defArgs.props;
  const action = isInstructor(userRole) ? AppAction.ASSIGNMENT_PREVIEW : AppAction.ASSESSMENT_START;
  const shadowOutProps = stripNilProps({
    ...sharedAppLinkParams,
    courseSection: course,
    courseSectionId: null,
    courseEntitlements: null
  });
  return Promise.resolve(
    navigateToApp({
      app: Application.SHADOW_HEALTH,
      includeLinkHash: true,
      action,
      body: shadowOutProps,
    })
  );
};

export const shadowHealthClickSettings = (props: CoursePlanClickSettings) => {
  const { assignment, defArgs, sharedAppLinkParams, sharedSearchParams, isDisableLinkToAMA } = props;
  const { redirect, navigateToApp } = defArgs.props;
  if (isDisableLinkToAMA) {
    redirect(addSearchParams(RoutePath.SHADOW_HEALTH_ASSIGNMENT_EDITOR, sharedSearchParams));
  } else {
    const action = assignment ? AppAction.ASSIGNMENT_EDIT : AppAction.ASSIGNMENT_CREATE;
    navigateToApp({
      app: Application.ASSIGNMENT_MANAGEMENT,
      includeLinkHash: true,
      action,
      body: sharedAppLinkParams,
    });
  }
};

export const shadowHealthPerformanceReportClick = (props: CoursePlanPerformanceReportClickProps) => {
  const { defArgs, sharedAppLinkParams } = props;
  const { navigateToApp, course } = defArgs.props;
  const action = AppAction.ASSIGNMENT_PERFORMANCE_VIEW;
  const shadowOutProps = stripNilProps({
    ...sharedAppLinkParams,
    courseSection: course,
    courseSectionId: null,
    courseEntitlements: null
  });
  navigateToApp({
    app: Application.SHADOW_HEALTH,
    includeLinkHash: true,
    action,
    body: shadowOutProps
  });
};

export const shadowHealthCatalogTitleClick = (props: CatalogPageTitleClickProps) => {
  const { defArgs, contentItem } = props;
  const { navigateToApp, course } = defArgs.props;
  navigateToApp({
    app: Application.SHADOW_HEALTH,
    action: AppAction.ASSIGNMENT_PREVIEW,
    includeLinkHash: true,
    body: stripNilProps({
      catalogItem: contentItem,
      ref: RoutePath.CATALOG,
      courseSection: course
    })
  });
};

export const getShadowHealthTitle = (props: RecContentItemTitleHandlerProps): string | null => {
  const shadowHealth = getShadowHealth(props.contentItem, props.catalog);
  if (shadowHealth) {
    return shadowHealth._parsedData.assignmentTitle;
  }
  return null;
};

export const getShadowHealthTitleClone = (props: RecContentItemTitleHandlerPropsClone): string | null => {
  const shadowHealth = getShadowHealthClone(props.contentItem, props.externalEntitiesMap);
  if (shadowHealth) {
    return shadowHealth._parsedData.assignmentTitle;
  }
  return null;
};

export const shadowHealthSortCatalogItemsWithoutTaxonomy = (props: SortCatalogItems): number | null => {
  const { contentItem, catalog } = props;
  const shadowHealth = getShadowHealth(contentItem, catalog);
  if (!shadowHealth) {
    return null;
  }
  return shadowHealth._parsedData.order;
};

export const shadowHealthSortCatalogItemsWithoutTaxonomyClone = (props: SortCatalogItems): number | null => {
  const { contentItem, externalEntitiesMap } = props;
  const shadowHealth = getShadowHealthClone(contentItem, externalEntitiesMap);
  if (!shadowHealth) {
    return null;
  }
  return shadowHealth._parsedData.order;
};

export const getExternalEntityCommon = (props: EntityProps) => {
  return getCommonExternalEntity(props);
};

export const commonAssignmentInfoHandler = (
  handler: (
    catalog: CatalogWithExternalEntitiesDto,
    contentItem: RecContentItemDto,
    primaryTaxonomies: PrimaryTaxonomy[],
    evolveProducts: EvolveProductDto[]
  ) => Partial<AssignmentDto>
) => (props: GetAssignmentInfoProps) => {
  const {
    newAssignment,
    syllabusItem,
    catalog,
    primaryTaxonomies,
    evolveProducts,
    fetchEolsCollectionObjectDataAction
  } = props;
  const contentItem = getCatalogItemFromSyllabusItem(catalog, syllabusItem);
  const assignmentInfo = handler(catalog, contentItem, primaryTaxonomies, evolveProducts);

  if (syllabusItem.type !== ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW) {
    assignmentInfo.assignmentGoals[0].goal = newAssignment.gradePoints;
  }

  const isAssignableSimulationSubTypes = contentItem && assignableSimulationSubTypes.includes(contentItem.type);

  if (syllabusItem.type === ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ
  || syllabusItem.type === ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ
  || syllabusItem.type === ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE
  || syllabusItem.type === ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE
  || syllabusItem.type === ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE
  || syllabusItem.type === ActiveSyllabusItemTypeDto.SBAR_EXERCISE
  || isAssignableSimulationSubTypes) {
    // EOLS Collection object data includes questions for Pre-Simulation and Post-Simulation Quiz assignments
    // and all Simulation Exercise sub-types (PRE_SIMULATION_EXERCISE, POST_SIMULATION_EXERCISE, JOURNAL_EXERCISE, SBAR_EXERCISE)
    return fetchEolsCollectionObjectDataAction(newAssignment.contentId)
      .then(questions => {
        const assignmentQuestions = questions.map((question, index) => ({
          questionVtwId: question.vtwId,
          displayOrder: index,
        }));

        const mergedAssignment = {
          ...newAssignment,
          ...assignmentInfo,
          assignmentQuestions,
          isGradePointIndependentGoals: true,
          title: newAssignment.title || assignmentInfo.title
        };
        if (mergedAssignment.assignmentGoals && mergedAssignment.assignmentGoals.length > 0) {
          mergedAssignment.assignmentGoals[0].goal = assignmentQuestions.length;
        }

        return mergedAssignment;
      });
  }

  return Promise.resolve({
    ...newAssignment,
    ...assignmentInfo,
    title: newAssignment.title || assignmentInfo.title
  });
};
